import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  Popconfirm,
  Select,
  Space,
  Spin,
  Tooltip,
  Typography,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
} from '@ant-design/icons';
import { OrganizationChart } from 'primereact/organizationchart';
import UserService from '../../services/UserService';
import ImageService from '../../services/ImageService';
import OrganizationChartService, {
  OrganizationChartNodeItem,
  OrganizationChartNodePayload,
} from '../../services/OrganizationChartService';
import { hasRole, openNotification } from '../../utils';
import './OrganizationChartSettings.scss';

const { Text } = Typography;

type DrawerMode = 'create-root' | 'create-child' | 'edit' | null;

type AccountOption = {
  id: number;
  firstName?: string;
  lastName?: string;
  email?: string;
  profilePhoto?: string;
  status?: boolean;
  enabled?: boolean;
  roles?: Array<{ name?: string }>;
};

const ALLOWED_ROLE_NAMES = new Set(['ADMIN', 'FINANCE', 'CENTRAL_USER', 'INSPECTOR']);
const DEFAULT_NODE_COLOR = '#1677FF';
const COLOR_HEX_REGEX = /^#[0-9A-Fa-f]{6}$/;
const MIN_ZOOM = 0.6;
const MAX_ZOOM = 1.8;
const ZOOM_STEP = 0.1;

const normalizeRoleName = (name: string) => {
  const normalized = String(name || '').trim().toUpperCase();
  if (normalized.startsWith('ROLE_')) {
    return normalized.slice(5);
  }
  return normalized;
};

const getRoleSummary = (account?: Partial<AccountOption>) => {
  const roleNames = (account?.roles || [])
    .map((role: any) => normalizeRoleName(String(role?.title || '')))
    .filter(Boolean);
  return roleNames.join(', ');
};

const isAllowedAccount = (account: AccountOption) => {
  const isActive = account?.status !== false && account?.enabled !== false;
  if (!isActive) {
    return false;
  }
  const roles = (account?.roles || [])
    .map((role) => normalizeRoleName(String(role?.name || '')))
    .filter(Boolean);
  return roles.some((roleName) => ALLOWED_ROLE_NAMES.has(roleName));
};

const getFullName = (item?: Partial<AccountOption | OrganizationChartNodeItem>) => {
  const fullName = `${item?.firstName || ''} ${item?.lastName || ''}`.trim();
  return fullName || item?.email || '-';
};

const getNodeDisplayName = (node?: Partial<OrganizationChartNodeItem>) => {
  const fullName = getFullName(node);
  if (fullName !== '-') {
    return fullName;
  }
  return node?.department || node?.positionTitle || 'Bölüm';
};

const flattenTree = (roots: OrganizationChartNodeItem[]): OrganizationChartNodeItem[] => {
  if (!Array.isArray(roots) || roots.length === 0) return [];
  const result: OrganizationChartNodeItem[] = [];
  const stack: OrganizationChartNodeItem[] = [...roots];

  while (stack.length > 0) {
    const current = stack.shift();
    if (!current) continue;
    result.push(current);
    if (Array.isArray(current.children) && current.children.length > 0) {
      stack.unshift(...current.children);
    }
  }

  return result;
};

const findNodeById = (roots: OrganizationChartNodeItem[], id: number): OrganizationChartNodeItem | null => {
  for (const root of roots) {
    if (root.id === id) return root;
    const foundInChild = findNodeById(root.children || [], id);
    if (foundInChild) return foundInChild;
  }
  return null;
};

const collectDescendantIds = (roots: OrganizationChartNodeItem[], id: number): Set<number> => {
  const target = findNodeById(roots, id);
  const ids = new Set<number>();
  if (!target) return ids;

  const stack = [...(target.children || [])];
  while (stack.length > 0) {
    const current = stack.pop();
    if (!current) continue;
    ids.add(current.id);
    if (current.children?.length) {
      stack.push(...current.children);
    }
  }
  return ids;
};

const toPrimeNode = (node: OrganizationChartNodeItem): any => ({
  key: String(node.id),
  data: node,
  expanded: true,
  children: (node.children || []).map(toPrimeNode),
});

const nodeColorToBackground = (hex: string) => {
  if (!hex || !COLOR_HEX_REGEX.test(hex)) {
    return '#EEF2FF';
  }
  return `${hex}22`;
};

const normalizeColorValue = (value?: string | null) => {
  const normalized = String(value || '').toUpperCase();
  return COLOR_HEX_REGEX.test(normalized) ? normalized : DEFAULT_NODE_COLOR;
};

const toOptionalNumber = (value: any): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return null;
  }
  return parsed;
};

type ColorPickerFieldProps = {
  value?: string;
  onChange?: (value: string) => void;
};

const ColorPickerField: React.FC<ColorPickerFieldProps> = ({ value, onChange }) => {
  const textValue = String(value || '').toUpperCase();
  const colorValue = normalizeColorValue(value);

  return (
    <div className="color-input-wrapper">
      <input
        className="color-input-native"
        type="color"
        value={colorValue}
        onChange={(event) => onChange?.(event.target.value.toUpperCase())}
      />
      <Input
        value={textValue}
        maxLength={7}
        placeholder="#RRGGBB"
        onChange={(event) => onChange?.(String(event.target.value || '').toUpperCase())}
      />
    </div>
  );
};

const OrganizationChartSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [chartRoots, setChartRoots] = useState<OrganizationChartNodeItem[]>([]);
  const [accounts, setAccounts] = useState<AccountOption[]>([]);
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({});
  const [drawerMode, setDrawerMode] = useState<DrawerMode>(null);
  const [selectedNode, setSelectedNode] = useState<OrganizationChartNodeItem | null>(null);
  const [zoomLevel, setZoomLevel] = useState(1);

  const [form] = Form.useForm();
  const isAdmin = useMemo(() => hasRole('ADMIN'), []);

  const flatNodes = useMemo(() => flattenTree(chartRoots), [chartRoots]);
  const primeNodes = useMemo(() => chartRoots.map(toPrimeNode), [chartRoots]);

  const selectedNodeId = selectedNode?.id;
  const hasRootNode = chartRoots.length > 0;

  const accountOptions = useMemo(
    () =>
      accounts
        .filter((account) => {
          const accountId = Number(account.id);
          return Number.isFinite(accountId) && accountId > 0;
        })
        .sort((first, second) =>
          getFullName(first).localeCompare(getFullName(second), 'tr', { sensitivity: 'base' }),
        )
        .map((account) => {
          const roleSummary = getRoleSummary(account);

          return {
            value: account.id,
            label: getFullName(account),
            description: `${account.email || '-'}${roleSummary ? ` • ${roleSummary}` : ''}`,
          };
        }),
    [accounts],
  );

  const parentOptions = useMemo(() => {
    if (drawerMode === 'create-root') {
      return [];
    }

    const descendantIds = drawerMode === 'edit' && selectedNodeId
      ? collectDescendantIds(chartRoots, selectedNodeId)
      : new Set<number>();

    return flatNodes
      .filter((node) => {
        if (drawerMode === 'edit' && selectedNodeId && node.id === selectedNodeId) {
          return false;
        }
        if (drawerMode === 'edit' && descendantIds.has(node.id)) {
          return false;
        }
        return true;
      })
      .sort((first, second) =>
        getNodeDisplayName(first).localeCompare(getNodeDisplayName(second), 'tr', { sensitivity: 'base' }),
      )
      .map((node) => ({
        value: node.id,
        label: `${getNodeDisplayName(node)} - ${node.positionTitle}`,
      }));
  }, [chartRoots, drawerMode, flatNodes, selectedNodeId]);

  const fetchChart = async () => {
    setLoading(true);
    try {
      const data = await OrganizationChartService.getOrganizationChart();
      setChartRoots(Array.isArray(data) ? data : []);
    } catch (error: any) {
      openNotification('error', 'Hata', error?.message || 'Organizasyon şeması yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAccounts = async () => {
    try {
      const response: any = await UserService.getUsersWithoutPagination({ status: true });
      const list: AccountOption[] = response?.data?.content ?? response?.data ?? [];
      const filtered = (list || []).filter(isAllowedAccount);
      setAccounts(filtered);
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi', error);
      openNotification('error', 'Hata', 'Kullanıcı listesi yüklenemedi.');
    }
  };

  useEffect(() => {
    fetchChart();
    fetchAccounts();
  }, []);

  useEffect(() => {
    const photoIds = new Set<string>();
    flatNodes.forEach((node) => {
      const photoId = node.profilePhoto;
      if (photoId) {
        photoIds.add(String(photoId));
      }
    });

    const missingPhotoIds = Array.from(photoIds).filter((photoId) => !photoUrls[photoId]);
    if (missingPhotoIds.length === 0) {
      return;
    }

    let cancelled = false;

    Promise.all(
      missingPhotoIds.map(async (photoId) => {
        try {
          const response: any = await ImageService.getImage(photoId);
          const url = response?.data?.preSignUrl;
          return url ? { photoId, url } : null;
        } catch {
          return null;
        }
      }),
    ).then((results) => {
      if (cancelled) return;
      const validItems = results.filter(Boolean) as Array<{ photoId: string; url: string }>;
      if (validItems.length === 0) return;
      setPhotoUrls((prev) => {
        const next = { ...prev };
        validItems.forEach((item) => {
          next[item.photoId] = item.url;
        });
        return next;
      });
    });

    return () => {
      cancelled = true;
    };
  }, [flatNodes, photoUrls]);

  const closeDrawer = () => {
    setDrawerMode(null);
    setSelectedNode(null);
    form.resetFields();
  };

  const openCreateRoot = () => {
    setDrawerMode('create-root');
    setSelectedNode(null);
    form.setFieldsValue({
      accountId: undefined,
      parentId: undefined,
      positionTitle: 'Genel Müdür',
      department: 'Genel Müdürlük',
      colorHex: DEFAULT_NODE_COLOR,
    });
  };

  const openCreateChild = (node: OrganizationChartNodeItem) => {
    setDrawerMode('create-child');
    setSelectedNode(node);
    form.setFieldsValue({
      accountId: undefined,
      parentId: node.id,
      positionTitle: '',
      department: '',
      colorHex: DEFAULT_NODE_COLOR,
    });
  };

  const openEdit = (node: OrganizationChartNodeItem) => {
    setDrawerMode('edit');
    setSelectedNode(node);
    form.setFieldsValue({
      accountId: node.accountId ?? undefined,
      parentId: node.parentId ?? undefined,
      positionTitle: node.positionTitle,
      department: node.department,
      colorHex: normalizeColorValue(node.colorHex),
    });
  };

  const handleDelete = async (node: OrganizationChartNodeItem) => {
    try {
      await OrganizationChartService.deleteOrganizationChartNode(node.id);
      openNotification('success', 'Başarılı', 'Bölüm silindi.');
      await fetchChart();
    } catch (error: any) {
      openNotification('error', 'Hata', error?.message || 'Bölüm silinemedi.');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      const payload: OrganizationChartNodePayload = {
        accountId: toOptionalNumber(values.accountId),
        parentId: toOptionalNumber(values.parentId),
        positionTitle: String(values.positionTitle || '').trim(),
        department: String(values.department || '').trim(),
        colorHex: normalizeColorValue(values.colorHex),
      };

      if (drawerMode === 'edit' && selectedNode) {
        await OrganizationChartService.updateOrganizationChartNode(selectedNode.id, payload);
        openNotification('success', 'Başarılı', 'Bölüm güncellendi.');
      } else {
        await OrganizationChartService.createOrganizationChartNode(payload);
        openNotification('success', 'Başarılı', 'Bölüm eklendi.');
      }

      closeDrawer();
      await fetchChart();
    } catch (error: any) {
      if (error?.errorFields) {
        return;
      }
      openNotification('error', 'Hata', error?.message || 'İşlem başarısız.');
    } finally {
      setSaving(false);
    }
  };

  const drawerTitle =
    drawerMode === 'create-root'
      ? 'Ana Bölüm Ekle'
      : drawerMode === 'create-child'
        ? 'Alt Bölüm Ekle'
        : 'Bölüm Düzenle';

  const handleZoomIn = () => {
    setZoomLevel((prev) => Math.min(MAX_ZOOM, Number((prev + ZOOM_STEP).toFixed(2))));
  };

  const handleZoomOut = () => {
    setZoomLevel((prev) => Math.max(MIN_ZOOM, Number((prev - ZOOM_STEP).toFixed(2))));
  };

  const nodeTemplate = (node: any) => {
    const data: OrganizationChartNodeItem = node?.data;
    const hasUser = data?.accountId !== null && data?.accountId !== undefined;
    const fullName = hasUser ? getFullName(data) : '-';
    const photoId = data?.profilePhoto ? String(data.profilePhoto) : '';
    const avatarUrl = photoId ? photoUrls[photoId] : '';
    const isLeaf = !Array.isArray(data?.children) || data.children.length === 0;

    return (
      <div
        className="org-node-card"
        style={{
          borderColor: data?.colorHex || DEFAULT_NODE_COLOR,
          backgroundColor: nodeColorToBackground(data?.colorHex || DEFAULT_NODE_COLOR),
        }}
      >
        <div className="org-node-head">
          <Avatar src={avatarUrl || undefined}>{fullName.charAt(0).toUpperCase()}</Avatar>
          <div>
            <div className="org-node-name">{fullName}</div>
            <div className="org-node-email">{data?.email || '-'}</div>
          </div>
        </div>

        <div className="org-node-meta">
          <div className="org-node-meta-item">
            <strong>Pozisyon:</strong>
            {data?.positionTitle || '-'}
          </div>
          <div className="org-node-meta-item">
            <strong>Bölüm:</strong>
            {data?.department || '-'}
          </div>
        </div>

        {isAdmin && (
          <div className="org-node-actions">
            <Tooltip title="Alt Ekle">
              <Button
                type="text"
                shape="circle"
                icon={<PlusOutlined />}
                className="org-node-icon-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  openCreateChild(data);
                }}
              />
            </Tooltip>
            <Tooltip title="Düzenle">
              <Button
                type="text"
                shape="circle"
                icon={<EditOutlined />}
                className="org-node-icon-btn"
                onClick={(event) => {
                  event.stopPropagation();
                  openEdit(data);
                }}
              />
            </Tooltip>
            <Popconfirm
              title="Bu bölümü silmek istediğinize emin misiniz?"
              okText="Sil"
              cancelText="Vazgeç"
              onConfirm={(event) => {
                event?.stopPropagation?.();
                return handleDelete(data);
              }}
              disabled={!isLeaf}
            >
              <Tooltip title="Sil">
                <Button
                  type="text"
                  shape="circle"
                  danger
                  icon={<DeleteOutlined />}
                  className="org-node-icon-btn"
                  disabled={!isLeaf}
                  onClick={(event) => event.stopPropagation()}
                />
              </Tooltip>
            </Popconfirm>
          </div>
        )}
      </div>
    );
  };

  return (
    <Card className="organization-chart-settings">
      <div className="organization-chart-header">
        <Text strong>Organizasyon Şeması</Text>
        <Space>
          <Tooltip title="Uzaklaştır">
            <Button
              shape="circle"
              icon={<ZoomOutOutlined />}
              onClick={handleZoomOut}
              disabled={zoomLevel <= MIN_ZOOM}
            />
          </Tooltip>
          <Text type="secondary">{`${Math.round(zoomLevel * 100)}%`}</Text>
          <Tooltip title="Yakınlaştır">
            <Button
              shape="circle"
              icon={<ZoomInOutlined />}
              onClick={handleZoomIn}
              disabled={zoomLevel >= MAX_ZOOM}
            />
          </Tooltip>
          {isAdmin && (
            <Tooltip title={hasRootNode ? 'Sistemde yalnızca bir ana bölüm eklenebilir.' : undefined}>
              <span>
                <Button
                  type="primary"
                  className="btn-inline"
                  onClick={openCreateRoot}
                  disabled={hasRootNode}
                >
                  Ana Bölüm Ekle
                </Button>
              </span>
            </Tooltip>
          )}
        </Space>
      </div>

      {!isAdmin && (
        <div className="organization-chart-readonly-note">
          Bu alanı görüntüleyebilirsiniz, düzenleme yalnızca ADMIN kullanıcılarına açıktır.
        </div>
      )}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : chartRoots.length === 0 ? (
        <div className="organization-chart-empty">
          Henüz organizasyon şeması tanımlanmadı.
        </div>
      ) : (
        <div className="organization-chart-wrapper">
          <div
            className="organization-chart-scale-layer"
            style={{ transform: `scale(${zoomLevel})` }}
          >
            <OrganizationChart value={primeNodes} nodeTemplate={nodeTemplate} />
          </div>
        </div>
      )}

      <Drawer
        title={drawerTitle}
        open={drawerMode !== null}
        onClose={closeDrawer}
        width={520}
        destroyOnClose
        footer={
          <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button onClick={closeDrawer}>İptal</Button>
            <Button type="primary" loading={saving} onClick={handleSubmit}>
              Kaydet
            </Button>
          </Space>
        }
      >
        <Form layout="vertical" form={form}>
          <Form.Item label="Kullanıcı" name="accountId">
            <Select
              showSearch
              allowClear
              placeholder="Kullanıcı seçiniz (isteğe bağlı)"
              optionFilterProp="label"
              options={accountOptions}
              filterOption={(input, option) => {
                const text = [
                  option?.label,
                  option?.description,
                ]
                  .join(' ')
                  .toLocaleLowerCase('tr-TR');

                return text.includes(input.toLocaleLowerCase('tr-TR'));
              }}
              optionRender={(option) => {
                const data = option.data;
                return (
                  <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span>{data.label}</span>
                    <span style={{ fontSize: 12, color: '#999' }}>
                      {data.description}
                    </span>
                  </div>
                );
              }}
            />
          </Form.Item>

          {drawerMode !== 'create-root' && (
            <Form.Item
              label="Üst Bölüm"
              name="parentId"
              tooltip="Boş bırakırsanız ana bölüm olur."
            >
              <Select
                showSearch
                allowClear
                placeholder="Ana seviye için boş bırakın"
                optionFilterProp="label"
                options={parentOptions}
                filterOption={(input, option) =>
                  String(option?.label || '')
                    .toLocaleLowerCase('tr-TR')
                    .includes(input.toLocaleLowerCase('tr-TR'))
                }
              />
            </Form.Item>
          )}

          <Form.Item
            label="Pozisyon"
            name="positionTitle"
            rules={[{ required: true, message: 'Pozisyon zorunludur.' }]}
          >
            <Input maxLength={255} placeholder="Örn: Müdür" />
          </Form.Item>

          <Form.Item
            label="Bölüm"
            name="department"
            rules={[{ required: true, message: 'Bölüm zorunludur.' }]}
          >
            <Input maxLength={255} placeholder="Örn: Operasyon" />
          </Form.Item>

          <Form.Item
            label="Kart Rengi"
            name="colorHex"
            rules={[
              { required: true, message: 'Kart rengi zorunludur.' },
              { pattern: COLOR_HEX_REGEX, message: 'Renk formatı #RRGGBB olmalıdır.' },
            ]}
          >
            <ColorPickerField />
          </Form.Item>
        </Form>
      </Drawer>
    </Card>
  );
};

export default OrganizationChartSettings;
